import React, { useState, useEffect } from 'react';
import SafetyNote from './SafetyNote';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  School, PlayArrow, Shuffle, Edit, Delete, Add, ArrowBack,
  Science, Sports, Favorite, MenuBook, Explore, EmojiEvents,
  LocalFlorist, Psychology, Biotech, Castle, Waves, MedicalServices
} from '@mui/icons-material';
import { flashcardService } from '../services/flashcardService';
import type { FlashcardDeck, Flashcard, FlashcardDeckGroup } from '../types/Flashcard';
import FlashcardPlayer from './FlashcardPlayer';
import FlashcardEditor from './FlashcardEditor';
import AppNavigation from './AppNavigation';
import SignInPromptDialog from './SignInPromptDialog';
import { preferencesManager } from '../utils/preferencesManager';
import { authService } from '../services/authService';
import { optionsManager, getCertificationLevel } from '../utils/optionsManager';
import { getFlashcardQuestionPreview, getFlashcardTypeLabel } from '../utils/flashcardUtils';
import { shuffleArray } from '../utils/random';

// Available deck icons
const DECK_ICONS = [
  { name: 'School', component: School, label: 'School' },
  { name: 'Science', component: Science, label: 'Science' },
  { name: 'Sports', component: Sports, label: 'Sports' },
  { name: 'MedicalServices', component: MedicalServices, label: 'First Aid' },
  { name: '/certifications/dan-first-aid.svg', label: 'DAN First Aid', isImage: true },
  { name: '/certifications/diving-physics.svg', label: 'Diving Physics', isImage: true },
  { name: 'Favorite', component: Favorite, label: 'Favorite' },
  { name: 'MenuBook', component: MenuBook, label: 'Book' },
  { name: 'Explore', component: Explore, label: 'Explore' },
  { name: 'EmojiEvents', component: EmojiEvents, label: 'Trophy' },
  { name: 'LocalFlorist', component: LocalFlorist, label: 'Nature' },
  { name: 'Psychology', component: Psychology, label: 'Psychology' },
  { name: 'Biotech', component: Biotech, label: 'Biotech' },
  { name: 'Castle', component: Castle, label: 'Castle' },
  { name: 'Waves', component: Waves, label: 'Waves' },
];

// Helper function to check if a string is a custom image URL
const isCustomImageUrl = (iconName?: string) => {
  return iconName && (iconName.startsWith('http') || iconName.startsWith('/'));
};

// Helper function to get icon component by name
const getIconComponent = (iconName?: string) => {
  const icon = DECK_ICONS.find(i => i.name === iconName);
  return icon?.component || School;
};

const ENABLE_FLASHCARD_MANAGEMENT = false;
const RANDOM_25_DECK_ID = 'random-25-cards';
const RANDOM_25_CARD_COUNT = 25;
const HIDDEN_DECK_IDS = new Set(['random-mix']);
const INSTRUCTOR_EXAM_DECK_ID = 'instructor-exam-prep';
const INSTRUCTOR_EXAM_CARD_COUNT = 25;
const INSTRUCTOR_SOURCE_DECK_IDS = ['open-water', 'advanced-diver', 'rescue-diver', 'custom-1774035867196', 'master-scuba-diver'];
const DEFAULT_DECK_GROUP: FlashcardDeckGroup = 'topics';
const CERTIFICATION_DECK_IDS = new Set([
  'advanced-diver',
  'dan-first-aid',
  'dive-master',
  'instructor',
  'instructor-exam-prep',
  'master-scuba-diver',
  'nitrox-diver',
  'open-water',
  'rescue-diver',
]);

const getDeckGroup = (deck: FlashcardDeck): FlashcardDeckGroup => {
  if (deck.group === 'topics' || deck.group === 'certifications') {
    return deck.group;
  }

  return CERTIFICATION_DECK_IDS.has(deck.deckId) ? 'certifications' : DEFAULT_DECK_GROUP;
};

// Maps deck ID to a numeric sort order (easiest → hardest). Lower = easier.
const CERTIFICATION_DECK_SORT_ORDER: Record<string, number> = {
  'open-water': 1,
  'advanced-diver': 2,
  'rescue-diver': 3,
  'master-scuba-diver': 4,
  'dive-master': 5,
  'dan-first-aid': 6,
  'nitrox-diver': 7,
  'instructor': 8,
  'instructor-exam-prep': 9,
};

// Deck IDs that are gated on gas certifications rather than the main cert level.
// Maps deck ID → the gas certification flag required.
const GAS_CERT_DECK_REQUIREMENTS: Record<string, keyof ReturnType<typeof optionsManager.getOptions>['diver']['gasCertifications']> = {
  'nitrox-diver': 'nitrox',
};

// Maps deck ID → the minimum certification required (maps to getCertificationLevel values).
// Decks not in this map are shown to everyone once they pass the gas cert check.
const CERT_DECK_MIN_LEVEL: Record<string, number> = {
  'open-water': 0,         // Always visible — first cert to work toward
  'advanced-diver': 2,     // AOW
  'rescue-diver': 3,       // Rescue
  'master-scuba-diver': 3, // Rescue (next step toward MSD)
  'dive-master': 4,        // DM
  'dan-first-aid': 1,      // Any certified diver
};

const Flashcards: React.FC = () => {
  const [signInPrompt, setSignInPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playingDeck, setPlayingDeck] = useState<FlashcardDeck | null>(null);
  const [loadingStats, setLoadingStats] = useState<string | null>(null);
  const [missedCounts, setMissedCounts] = useState<Record<string, number>>({});
  const [missedCardIdsByDeck, setMissedCardIdsByDeck] = useState<Record<string, string[]>>({});
  const [editingDeck, setEditingDeck] = useState<FlashcardDeck | null>(null);
  const [editingCard, setEditingCard] = useState<{ deck: FlashcardDeck; card?: Flashcard } | null>(null);
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [creatingDeck, setCreatingDeck] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const [newDeckGroup, setNewDeckGroup] = useState<FlashcardDeckGroup>(DEFAULT_DECK_GROUP);
  const [newDeckIcon, setNewDeckIcon] = useState('School');
  const [customIconUrl, setCustomIconUrl] = useState('');
  const [editingDeckIcon, setEditingDeckIcon] = useState(false);
  const [editCustomIconUrl, setEditCustomIconUrl] = useState('');

  // Load all decks on mount
  useEffect(() => {
    const loadAllDecks = async () => {
      try {
        const allDecks = await flashcardService.getAllDecks();
        setDecks(allDecks.filter((deck) => !HIDDEN_DECK_IDS.has(deck.deckId)));
      } catch (err) {
        console.error('Failed to load decks:', err);
      }
    };
    loadAllDecks();
  }, []);

  useEffect(() => {
    if (playingDeck || editingDeck || editingCard) {
      return;
    }

    if (decks.length === 0) {
      setMissedCounts({});
      setMissedCardIdsByDeck({});
      return;
    }

    let cancelled = false;

    const loadMissedCounts = async () => {
      const userId = preferencesManager.getUserId() || preferencesManager.getDemoUserId();

      const counts = await Promise.all(
        decks.map(async (deck) => {
          const stats = await flashcardService.getStats(userId, deck.deckId);
          const missedCardIds = Object.entries(stats.cardStats)
            .filter(([_, stat]) => stat.incorrectCount > 0)
            .map(([cardId]) => cardId);

          return {
            deckId: deck.deckId,
            missedCount: missedCardIds.length,
            missedCardIds,
          };
        })
      );

      if (!cancelled) {
        setMissedCounts(
          Object.fromEntries(counts.map(({ deckId, missedCount }) => [deckId, missedCount]))
        );
        setMissedCardIdsByDeck(
          Object.fromEntries(counts.map(({ deckId, missedCardIds }) => [deckId, missedCardIds]))
        );
      }
    };

    loadMissedCounts();

    return () => {
      cancelled = true;
    };
  }, [decks, playingDeck, editingDeck, editingCard]);

  const handleSaveCard = async (card: Flashcard) => {
    if (!editingCard || !editingDeck) return;
    if (!authService.isAuthenticated()) { setSignInPrompt('Saving custom flashcards'); return; }

    try {
      const deck = editingDeck;
      const existingCardIndex = deck.cards.findIndex(c => c.cardId === card.cardId);

      let updatedCards: Flashcard[];
      if (existingCardIndex >= 0) {
        updatedCards = [...deck.cards];
        updatedCards[existingCardIndex] = card;
      } else {
        updatedCards = [...deck.cards, card];
      }

      const updatedDeck: FlashcardDeck = {
        ...deck,
        cards: updatedCards,
        updatedAt: new Date().toISOString(),
      };

      await flashcardService.saveDeck(updatedDeck);
      setEditingDeck(updatedDeck);

      // Update deck in list
      setDecks(decks.map(d => d.deckId === updatedDeck.deckId ? updatedDeck : d));

      setEditingCard(null);
    } catch (err) {
      console.error('Failed to save card:', err);
      setError('Failed to save card');
    }
  };

  const handleDeleteCard = async (card: Flashcard) => {
    if (!editingDeck) return;
    if (!authService.isAuthenticated()) { setSignInPrompt('Deleting flashcards'); return; }

    if (!window.confirm('Are you sure you want to delete this card?')) {
      return;
    }

    try {
      const updatedDeck: FlashcardDeck = {
        ...editingDeck,
        cards: editingDeck.cards.filter(c => c.cardId !== card.cardId),
        updatedAt: new Date().toISOString(),
      };

      await flashcardService.saveDeck(updatedDeck);
      setEditingDeck(updatedDeck);

      // Update deck in list
      setDecks(decks.map(d => d.deckId === updatedDeck.deckId ? updatedDeck : d));
    } catch (err) {
      console.error('Failed to delete card:', err);
      setError('Failed to delete card');
    }
  };

  const handleUpdateDeckIcon = async (iconName: string) => {
    if (!editingDeck) return;
    if (!authService.isAuthenticated()) { setSignInPrompt('Editing custom decks'); return; }

    try {
      const updatedDeck: FlashcardDeck = {
        ...editingDeck,
        iconUrl: iconName,
        updatedAt: new Date().toISOString(),
      };

      await flashcardService.saveDeck(updatedDeck);
      setEditingDeck(updatedDeck);

      // Update deck in list
      setDecks(decks.map(d => d.deckId === updatedDeck.deckId ? updatedDeck : d));
      setEditingDeckIcon(false);
    } catch (err) {
      console.error('Failed to update deck icon:', err);
      setError('Failed to update deck icon');
    }
  };

  const handleUpdateDeckGroup = async (nextGroup: FlashcardDeckGroup | null) => {
    if (!editingDeck || !nextGroup || getDeckGroup(editingDeck) === nextGroup) {
      return;
    }
    if (!authService.isAuthenticated()) { setSignInPrompt('Editing custom decks'); return; }

    try {
      const updatedDeck: FlashcardDeck = {
        ...editingDeck,
        group: nextGroup,
        updatedAt: new Date().toISOString(),
      };

      await flashcardService.saveDeck(updatedDeck);
      setEditingDeck(updatedDeck);
      setDecks(decks.map(d => d.deckId === updatedDeck.deckId ? updatedDeck : d));
    } catch (err) {
      console.error('Failed to update deck group:', err);
      setError('Failed to update deck group');
    }
  };

  const handleStartCreatingDeck = () => {
    if (!authService.isAuthenticated()) { setSignInPrompt('Creating custom flashcard decks'); return; }
    setCreatingDeck(true);
    setNewDeckName('');
    setNewDeckGroup(DEFAULT_DECK_GROUP);
    setError(null);
  };

  const handleCancelCreatingDeck = () => {
    setCreatingDeck(false);
    setNewDeckName('');
    setNewDeckGroup(DEFAULT_DECK_GROUP);
    setNewDeckIcon('School');
    setCustomIconUrl('');
  };

  const handleCreateDeck = async () => {
    if (!authService.isAuthenticated()) { setSignInPrompt('Creating custom flashcard decks'); return; }
    if (!newDeckName.trim()) {
      setError('Deck name is required');
      return;
    }

    try {
      const userId = preferencesManager.getUserId() || preferencesManager.getDemoUserId();

      const newDeck: FlashcardDeck = {
        userId,
        deckId: `custom-${Date.now()}`,
        deckName: newDeckName.trim(),
        group: newDeckGroup,
        iconUrl: customIconUrl || newDeckIcon,
        cards: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await flashcardService.saveDeck(newDeck);

      // Add to decks list
      setDecks([...decks, newDeck]);

      // Close dialog and open in edit mode
      setCreatingDeck(false);
      setNewDeckName('');
      setNewDeckGroup(DEFAULT_DECK_GROUP);
      setNewDeckIcon('School');
      setCustomIconUrl('');
      setEditingDeck(newDeck);
    } catch (err) {
      console.error('Failed to create deck:', err);
      setError('Failed to create deck. Please try again.');
    }
  };

  const handleDeleteDeck = async (deck: FlashcardDeck) => {
    if (!authService.isAuthenticated()) { setSignInPrompt('Deleting custom decks'); return; }
    if (!window.confirm(`Are you sure you want to delete "${deck.deckName}"?`)) {
      return;
    }

    try {
      await flashcardService.deleteDeck(deck.deckId);

      // Remove from decks list
      setDecks(decks.filter(d => d.deckId !== deck.deckId));
    } catch (err) {
      console.error('Failed to delete deck:', err);
      setError('Failed to delete deck. Please try again.');
    }
  };

  const handleRandom25Cards = async (deck: FlashcardDeck) => {
    const randomDeckId = `${deck.deckId}::${RANDOM_25_DECK_ID}`;

    try {
      setLoading(randomDeckId);
      setError(null);

      if (deck.cards.length < RANDOM_25_CARD_COUNT) {
        setError(`${deck.deckName} needs at least ${RANDOM_25_CARD_COUNT} cards for Random 25 Cards.`);
        return;
      }

      const availableCards = deck.cards.map((card) => ({
          ...card,
          cardId: `${deck.deckId}::${card.cardId}`,
          sourceCardId: card.cardId,
          sourceDeckId: deck.deckId,
          sourceDeckName: deck.deckName,
        }));

      const selectedCards = shuffleArray(availableCards).slice(0, RANDOM_25_CARD_COUNT);

      setPlayingDeck({
        ...deck,
        deckId: randomDeckId,
        deckName: `${deck.deckName} - Random 25 Cards`,
        cards: selectedCards,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Failed to create Random 25 Cards:', err);
      setError('Failed to create Random 25 Cards. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleStartInstructorExam = async (deck: FlashcardDeck) => {
    try {
      setLoading(INSTRUCTOR_EXAM_DECK_ID);
      setError(null);

      const sourceDeckPromises = INSTRUCTOR_SOURCE_DECK_IDS.map(id => flashcardService.getDeck(id));
      const sourceDecks = (await Promise.all(sourceDeckPromises)).filter(Boolean) as FlashcardDeck[];

      const allCards = sourceDecks.flatMap(sourceDeck =>
        sourceDeck.cards.map(card => ({
          ...card,
          cardId: `${sourceDeck.deckId}::${card.cardId}`,
          sourceCardId: card.cardId,
          sourceDeckId: sourceDeck.deckId,
          sourceDeckName: sourceDeck.deckName,
        }))
      );

      if (allCards.length < INSTRUCTOR_EXAM_CARD_COUNT) {
        setError('Not enough source cards available to build an exam.');
        return;
      }

      const selectedCards = shuffleArray(allCards).slice(0, INSTRUCTOR_EXAM_CARD_COUNT);

      setPlayingDeck({
        ...deck,
        deckId: `${INSTRUCTOR_EXAM_DECK_ID}::exam`,
        deckName: `${deck.deckName} - ${INSTRUCTOR_EXAM_CARD_COUNT} Questions`,
        cards: selectedCards,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error('Failed to build instructor exam:', err);
      setError('Failed to build exam. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleRetryMisses = async (deck: FlashcardDeck) => {
    if (!authService.isAuthenticated()) { setSignInPrompt('Tracking missed cards'); return; }
    try {
      setLoadingStats(deck.deckId);
      setError(null);

      const userId = preferencesManager.getUserId() || preferencesManager.getDemoUserId();
      const stats = await flashcardService.getStats(userId, deck.deckId);

      const missedCardIds = new Set(
        Object.entries(stats.cardStats)
          .filter(([_, stat]) => stat.incorrectCount > 0)
          .map(([cardId, _]) => cardId)
      );

      if (missedCardIds.size === 0) {
        setError('No missed questions found! You\'re doing great!');
        setLoadingStats(null);
        return;
      }

      const retryDeck: FlashcardDeck = {
        ...deck,
        cards: deck.cards.filter(card => missedCardIds.has(card.cardId)),
        deckName: `${deck.deckName} - Retry (${missedCardIds.size} cards)`
      };

      setPlayingDeck(retryDeck);
    } catch (err) {
      console.error(`Failed to load retry deck for ${deck.deckName}:`, err);
      setError('Failed to load retry deck. Please try again.');
    } finally {
      setLoadingStats(null);
    }
  };

  const handleRetryAllMisses = () => {
    const retryCards = decks.flatMap((deck) => {
      const missedCardIds = new Set(missedCardIdsByDeck[deck.deckId] || []);

      return deck.cards
        .filter((card) => missedCardIds.has(card.cardId))
        .map((card) => ({
          ...card,
          cardId: `${deck.deckId}::${card.cardId}`,
          sourceCardId: card.cardId,
          sourceDeckId: deck.deckId,
          sourceDeckName: deck.deckName,
        }));
    });

    if (retryCards.length === 0) {
      setError('No missed questions found! You\'re doing great!');
      return;
    }

    setError(null);
    setPlayingDeck({
      deckId: 'retry-all-misses',
      deckName: 'Retry All My Misses',
      iconUrl: 'Psychology',
      cards: retryCards,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  // If playing flashcards, show the player
  if (playingDeck) {
    return (
      <FlashcardPlayer
        deck={playingDeck}
        onClose={() => setPlayingDeck(null)}
      />
    );
  }

  // If editing a card, show the editor
  if (ENABLE_FLASHCARD_MANAGEMENT && editingCard) {
    return (
      <FlashcardEditor
        card={editingCard.card}
        deckId={editingCard.deck.deckId}
        onSave={handleSaveCard}
        onCancel={() => setEditingCard(null)}
      />
    );
  }

  // If editing a deck, show the card list
  if (ENABLE_FLASHCARD_MANAGEMENT && editingDeck) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <AppNavigation />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => setEditingDeck(null)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Edit: {editingDeck.deckName}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setEditingCard({ deck: editingDeck })}
          >
            Add Card
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {(() => {
              const iconUrl = editingDeck.iconUrl;
              if (isCustomImageUrl(iconUrl)) {
                return (
                  <Box
                    component="img"
                    src={iconUrl}
                    alt="Deck icon"
                    sx={{ width: 40, height: 40, objectFit: 'contain' }}
                  />
                );
              }
              const DeckIcon = getIconComponent(iconUrl);
              return <DeckIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
            })()}
            <Chip
              label={`${editingDeck.cards.length} cards`}
              color="primary"
            />
            <Chip
              label={getDeckGroup(editingDeck) === 'certifications' ? 'Certification' : 'Topic'}
              variant="outlined"
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={() => setEditingDeckIcon(!editingDeckIcon)}
          >
            {editingDeckIcon ? 'Cancel' : 'Change Icon'}
          </Button>
        </Box>

        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Deck Group
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose whether this deck appears in Topics or Certifications on the flashcards page.
          </Typography>
          <ToggleButtonGroup
            value={getDeckGroup(editingDeck)}
            exclusive
            onChange={(_, value: FlashcardDeckGroup | null) => handleUpdateDeckGroup(value)}
            size="small"
            color="primary"
          >
            <ToggleButton value="topics">Topics</ToggleButton>
            <ToggleButton value="certifications">Certifications</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {editingDeckIcon && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select a New Icon
            </Typography>
            <Grid container spacing={1}>
              {DECK_ICONS.map((icon) => {
                const isSelected = editingDeck.iconUrl === icon.name;
                return (
                  <Grid item xs={3} sm={2} md={1.5} key={icon.name}>
                    <Box
                      onClick={() => handleUpdateDeckIcon(icon.name)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1.5,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.light' : 'action.hover',
                        },
                      }}
                    >
                      {icon.isImage ? (
                        <Box
                          component="img"
                          src={icon.name}
                          alt={icon.label}
                          sx={{
                            width: 28,
                            height: 28,
                            objectFit: 'contain',
                          }}
                        />
                      ) : icon.component ? (
                        (() => {
                          const IconComponent = icon.component;
                          return (
                            <IconComponent
                              sx={{
                                fontSize: 28,
                                color: isSelected ? 'primary.contrastText' : 'text.primary',
                              }}
                            />
                          );
                        })()
                      ) : null}
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          fontSize: '0.65rem',
                          color: isSelected ? 'primary.contrastText' : 'text.secondary',
                        }}
                      >
                        {icon.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Or enter custom image URL"
                value={editCustomIconUrl}
                onChange={(e) => setEditCustomIconUrl(e.target.value)}
                fullWidth
                placeholder="https://example.com/icon.png or /path/to/icon.svg"
                helperText="Enter a URL to use a custom image instead of an icon"
              />
              {editCustomIconUrl && (
                <Button
                  variant="contained"
                  onClick={() => {
                    handleUpdateDeckIcon(editCustomIconUrl);
                    setEditCustomIconUrl('');
                  }}
                  sx={{ mt: 1 }}
                >
                  Use Custom Image
                </Button>
              )}
            </Box>
          </Box>
        )}

        <List>
          {editingDeck.cards.map((card, index) => (
            <Card key={card.cardId} sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={`${index + 1}. ${getFlashcardQuestionPreview(card)}`}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Type: {getFlashcardTypeLabel(card)}
                      </Typography>
                      {card.imageUrl && (
                        <Typography variant="body2" color="text.secondary">
                          Has image
                        </Typography>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => setEditingCard({ deck: editingDeck, card })}
                    sx={{ mr: 1 }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteCard(card)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Card>
          ))}
        </List>

        {editingDeck.cards.length === 0 && (
          <Alert severity="info">
            No cards in this deck yet. Click "Add Card" to get started!
          </Alert>
        )}
      </Box>
    );
  }

  // Main flashcards page
  const sortedDecks = [...decks].sort((leftDeck, rightDeck) => {
    const leftMissedCount = missedCounts[leftDeck.deckId] ?? 0;
    const rightMissedCount = missedCounts[rightDeck.deckId] ?? 0;

    if (leftMissedCount !== rightMissedCount) {
      return rightMissedCount - leftMissedCount;
    }

    return leftDeck.deckName.localeCompare(rightDeck.deckName);
  });
  const topicDecks = sortedDecks.filter((deck) => getDeckGroup(deck) === 'topics');

  // Build certification deck list: filtered by user's cert level + gas certs, sorted easiest → hardest
  const userOptions = optionsManager.getOptions();
  const userCertLevel = getCertificationLevel(userOptions.diver.certification);
  const userGasCerts = userOptions.diver.gasCertifications;

  const allCertDecks = decks.filter((deck) => getDeckGroup(deck) === 'certifications');

  const certificationDecks = allCertDecks
    .filter((deck) => {
      // Gas cert decks are gated on their specific gas certification
      const gasCertRequired = GAS_CERT_DECK_REQUIREMENTS[deck.deckId];
      if (gasCertRequired !== undefined) {
        return userGasCerts[gasCertRequired];
      }

      // Uncategorized certification decks (e.g. user-created) are always shown
      const minLevel = CERT_DECK_MIN_LEVEL[deck.deckId];
      if (minLevel === undefined) return true;

      // Show decks at/below the user's level, plus the next level up (1 step above current)
      const deckSortOrder = CERTIFICATION_DECK_SORT_ORDER[deck.deckId] ?? 999;
      const userHighestDeckOrder = Math.max(
        0,
        ...Object.entries(CERT_DECK_MIN_LEVEL)
          .filter(([, lvl]) => lvl <= userCertLevel)
          .map(([id]) => CERTIFICATION_DECK_SORT_ORDER[id] ?? 0)
      );
      return deckSortOrder <= userHighestDeckOrder + 1;
    })
    .sort((a, b) => {
      const orderA = CERTIFICATION_DECK_SORT_ORDER[a.deckId] ?? 999;
      const orderB = CERTIFICATION_DECK_SORT_ORDER[b.deckId] ?? 999;
      return orderA - orderB;
    });
  const totalMissedCount = Object.values(missedCounts).reduce((sum, count) => sum + count, 0);
  const decksWithMissesCount = Object.values(missedCounts).filter((count) => count > 0).length;

  const renderDeckCard = (deck: FlashcardDeck) => {
    const missedCount = missedCounts[deck.deckId] ?? 0;
    const hasMisses = missedCount > 0;
    const randomDeckId = `${deck.deckId}::${RANDOM_25_DECK_ID}`;
    const actionButtonSx = { width: '100%' };

    return (
      <Grid item xs={12} sm={6} md={4} key={deck.deckId}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: hasMisses ? 'warning.main' : 'divider',
            boxShadow: hasMisses ? 4 : 1,
            bgcolor: hasMisses ? 'warning.50' : 'background.paper',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: hasMisses ? 6 : 4,
            },
          }}
        >
          <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
            <Box sx={{ mb: 2 }}>
              <Badge
                color="warning"
                badgeContent={hasMisses ? missedCount : 0}
                invisible={!hasMisses}
                max={99}
              >
                {(() => {
                  const iconUrl = deck.iconUrl;
                  if (isCustomImageUrl(iconUrl)) {
                    return (
                      <Box
                        component="img"
                        src={iconUrl}
                        alt="Deck icon"
                        sx={{ height: { xs: 120, sm: 150 }, maxWidth: '100%', objectFit: 'contain' }}
                      />
                    );
                  }
                  const DeckIcon = getIconComponent(iconUrl);
                  return <DeckIcon sx={{ fontSize: { xs: 120, sm: 150 }, color: hasMisses ? 'warning.dark' : 'primary.main' }} />;
                })()}
              </Badge>
            </Box>
            <Typography variant="h6" gutterBottom>
              {deck.deckName}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={deck.deckId === INSTRUCTOR_EXAM_DECK_ID ? 'Dynamic — draws from 5 decks' : `${deck.cards.length} questions`}
                color="primary"
                size="small"
                variant="outlined"
              />
              <Chip
                label={missedCount > 0 ? `${missedCount} missed` : 'No misses'}
                color={missedCount > 0 ? 'warning' : 'default'}
                size="small"
              />
            </Box>
          </CardContent>
          <CardActions
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              pb: 2,
              px: 2,
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              startIcon={loading === INSTRUCTOR_EXAM_DECK_ID ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
              onClick={() => deck.deckId === INSTRUCTOR_EXAM_DECK_ID ? handleStartInstructorExam(deck) : setPlayingDeck(deck)}
              fullWidth
              sx={actionButtonSx}
              disabled={(deck.deckId !== INSTRUCTOR_EXAM_DECK_ID && deck.cards.length === 0) || (deck.deckId === INSTRUCTOR_EXAM_DECK_ID && loading === INSTRUCTOR_EXAM_DECK_ID)}
            >
              {deck.deckId === INSTRUCTOR_EXAM_DECK_ID
                ? (loading === INSTRUCTOR_EXAM_DECK_ID ? 'Building Exam...' : 'Start Exam')
                : 'Start Studying'}
            </Button>
            {deck.deckId !== INSTRUCTOR_EXAM_DECK_ID && (
              <Button
                variant="outlined"
                startIcon={loading === randomDeckId ? <CircularProgress size={20} color="inherit" /> : <Shuffle />}
                onClick={() => handleRandom25Cards(deck)}
                disabled={loading !== null || loadingStats !== null || deck.cards.length < RANDOM_25_CARD_COUNT}
                fullWidth
                sx={actionButtonSx}
              >
                {loading === randomDeckId ? 'Building Random 25 Cards...' : 'Random 25 Cards'}
              </Button>
            )}
            {hasMisses && (
              <Button
                variant="outlined"
                startIcon={loadingStats === deck.deckId ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                onClick={() => handleRetryMisses(deck)}
                disabled={loading !== null || loadingStats !== null}
                fullWidth
                sx={actionButtonSx}
              >
                {loadingStats === deck.deckId ? 'Loading...' : `Retry My Misses (${missedCount})`}
              </Button>
            )}
            {ENABLE_FLASHCARD_MANAGEMENT && (
              <Box sx={{ display: 'flex', gap: 1, width: '100%', mt: 'auto' }}>
                {deck.deckId !== INSTRUCTOR_EXAM_DECK_ID && (
                  <Button
                    variant="text"
                    startIcon={<Edit />}
                    onClick={() => setEditingDeck(deck)}
                    fullWidth
                  >
                    Edit
                  </Button>
                )}
                <Button
                  variant="text"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => handleDeleteDeck(deck)}
                  fullWidth
                >
                  Delete
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const renderDeckSection = (
    title: string,
    description: string,
    sectionDecks: FlashcardDeck[],
    emptyMessage: string
  ) => (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Chip label={`${sectionDecks.length} decks`} size="small" variant="outlined" color="primary" />
      </Box>

      {sectionDecks.length === 0 ? (
        <Alert severity="info">{emptyMessage}</Alert>
      ) : (
        <Grid container spacing={3}>
          {sectionDecks.map(renderDeckCard)}
        </Grid>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <AppNavigation />
      <SafetyNote kind="studyAid" sx={{ textAlign: 'center', px: 2, pt: 1 }} />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <School /> Flashcards
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`${decks.length} decks`} size="small" variant="outlined" color="primary" />
            <Chip label={`${totalMissedCount} active misses`} size="small" color={totalMissedCount > 0 ? 'warning' : 'default'} />
          </Box>
        </Box>
        {ENABLE_FLASHCARD_MANAGEMENT && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleStartCreatingDeck}
            color="primary"
          >
            Create New Deck
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
        Pick a deck, start a Random 25 Cards run from that deck, or clear your active misses. Progress updates automatically as you practice.
      </Typography>

      {totalMissedCount > 0 && (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'warning.main',
                boxShadow: 5,
                bgcolor: 'warning.50',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 7,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <Badge
                    color="warning"
                    badgeContent={totalMissedCount}
                    max={999}
                  >
                    <Psychology sx={{ fontSize: 48, color: 'warning.dark' }} />
                  </Badge>
                </Box>
                <Typography variant="h6" gutterBottom>
                  Retry All My Misses
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip
                    label={`${totalMissedCount} missed cards`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    label={`${decksWithMissesCount} decks affected`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
              <CardActions
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  pb: 2,
                  px: 2,
                  flexDirection: 'column',
                  gap: 1,
                  minHeight: ENABLE_FLASHCARD_MANAGEMENT ? 198 : 152,
                }}
              >
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PlayArrow />}
                  onClick={handleRetryAllMisses}
                  disabled={loading !== null || loadingStats !== null}
                  fullWidth
                  sx={{ width: '100%' }}
                >
                  {`Retry All My Misses (${totalMissedCount})`}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      )}

      {renderDeckSection(
        'Topics',
        'Concept-focused decks for physics, physiology, mixed review, and other study themes.',
        topicDecks,
        'No topic decks are available yet.'
      )}

      {renderDeckSection(
        'Certifications',
        'Certification-oriented decks based on your current certification level, from easiest to hardest.',
        certificationDecks.filter((d) => !GAS_CERT_DECK_REQUIREMENTS[d.deckId]),
        'No certification decks available for your current level.'
      )}

      {Object.values(userGasCerts).some(Boolean) && (() => {
        const gasCertDecks = certificationDecks.filter((d) => GAS_CERT_DECK_REQUIREMENTS[d.deckId]);
        if (gasCertDecks.length === 0) return null;
        return renderDeckSection(
          'Gas Certifications',
          'Specialty gas certification decks (Nitrox, Trimix, etc.) based on your gas certifications.',
          gasCertDecks,
          'No gas certification decks available.'
        );
      })()}

      {/* Create New Deck Dialog */}
      <Dialog open={ENABLE_FLASHCARD_MANAGEMENT && creatingDeck} onClose={handleCancelCreatingDeck} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Flashcard Deck</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Deck Name"
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              fullWidth
              required
              autoFocus
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" gutterBottom>
              Deck Group
            </Typography>
            <ToggleButtonGroup
              value={newDeckGroup}
              exclusive
              onChange={(_, value: FlashcardDeckGroup | null) => {
                if (value) {
                  setNewDeckGroup(value);
                }
              }}
              size="small"
              color="primary"
              sx={{ mb: 3 }}
            >
              <ToggleButton value="topics">Topics</ToggleButton>
              <ToggleButton value="certifications">Certifications</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="subtitle2" gutterBottom>
              Choose an Icon
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {DECK_ICONS.map((icon) => {
                const isSelected = newDeckIcon === icon.name && !customIconUrl;
                return (
                  <Grid item xs={3} key={icon.name}>
                    <Box
                      onClick={() => {
                        setNewDeckIcon(icon.name);
                        setCustomIconUrl('');
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.light' : 'action.hover',
                        },
                      }}
                    >
                      {icon.isImage ? (
                        <Box
                          component="img"
                          src={icon.name}
                          alt={icon.label}
                          sx={{
                            width: 32,
                            height: 32,
                            objectFit: 'contain',
                          }}
                        />
                      ) : icon.component ? (
                        (() => {
                          const IconComponent = icon.component;
                          return (
                            <IconComponent
                              sx={{
                                fontSize: 32,
                                color: isSelected ? 'primary.contrastText' : 'text.primary',
                              }}
                            />
                          );
                        })()
                      ) : null}
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          fontSize: '0.65rem',
                          color: isSelected ? 'primary.contrastText' : 'text.secondary',
                        }}
                      >
                        {icon.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            <TextField
              label="Or enter custom image URL"
              value={customIconUrl}
              onChange={(e) => setCustomIconUrl(e.target.value)}
              fullWidth
              placeholder="https://example.com/icon.png or /certifications/icon.svg"
              helperText="Enter a URL to use a custom image instead of an icon"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreatingDeck}>Cancel</Button>
          <Button onClick={handleCreateDeck} variant="contained" disabled={!newDeckName.trim()}>
            Create Deck
          </Button>
        </DialogActions>
      </Dialog>

      <SignInPromptDialog
        open={signInPrompt !== null}
        onClose={() => setSignInPrompt(null)}
        action={signInPrompt ?? undefined}
      />
    </Box>
  );
};

export default Flashcards;
